import axios from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';
import { getStoredTokens, clearTokens } from './auth/tokenStorage';
import { refreshAccessToken } from './auth/tokenManager';

export const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

// Attach the current access token on every request.
api.interceptors.request.use((config) => {
  const tokens = getStoredTokens();
  if (tokens?.accessToken) {
    config.headers.Authorization = `Bearer ${tokens.accessToken}`;
  }
  return config;
});

// Single-flight 401 → refresh → retry. If refresh fails, clear tokens and
// let the app redirect to /login (done by ProtectedRoute after state updates).
interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error?.config as RetriableConfig | undefined;
    const status = error?.response?.status;
    if (status !== 401 || !original || original._retry) {
      return Promise.reject(error);
    }
    original._retry = true;
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      clearTokens();
      // Hard navigation — we're outside React render at this point.
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
    original.headers = original.headers ?? {};
    original.headers.Authorization = `Bearer ${refreshed.accessToken}`;
    return api.request(original);
  }
);

export interface Nomination {
  nomination_uuid: string;
  nomination_code: string;
  year: string;
  category_name: string;
  category_status: string;
  shortlisted_at: string | null;
  project_title: string;
  project_city: string;
  project_country: string;
  architect_company_name: string;
  architect_company_country: string;
  lead_architect_name: string;
  client_name: string;
  google_maps_link: string;
  project_completion_date: string;
  project_summary: string;
  hero_image: string;
  // Per-image credits are NOT on the nomination — they live on each image row
  // (see ImageInfo.credit from /nomination-images). The old flat
  // hero_image_credit / image_N_credit fields were dropped in NOC-397.
  a2board1: string;
  a2board2: string;
  video_landscape_url: string;
  video_portrait_url: string;
  video_download_url: string;
  video_copyright: string;
  // WBD award taxonomies, multi-valued: "|"-joined WBD term names (prize names
  // can contain commas). category_status is ", "-joined. See lib/taxonomies.ts.
  special_prizes: string | null;
  wafx_awards: string | null;
  prize_statuses: string | null;
  [key: string]: string | number | null | undefined;
}

/** One World Buildings Directory taxonomy term (parent = parent term's slug). */
export interface WbdTerm {
  name: string;
  slug: string;
  parent: string | null;
}

/** WBD taxonomy term lists keyed by WBD taxonomy name (waf_category, waf_prize, …). */
export type WbdTaxonomies = Record<string, WbdTerm[]>;

export async function getTaxonomies(): Promise<WbdTaxonomies> {
  const res = await api.get<WbdTaxonomies>('/taxonomies');
  return res.data;
}

export interface ImageInfo {
  /** DB row id — used to target a single image for authenticated download. */
  id: number;
  type: 'hero' | 'normal' | 'a2board1' | 'a2board2' | string;
  url: string;
  /** Photographer / image source, captured from Evessio at import. Null when the
   *  entrant didn't supply one. Shown under each image for all viewers (NOC-394). */
  credit: string | null;
}

export interface ListNominationsOptions {
  /** Cap the number of rows the server returns. Server clamps to [1, 500]. */
  limit?: number;
}

export async function listNominations(opts: ListNominationsOptions = {}): Promise<Nomination[]> {
  const params: Record<string, string> = {};
  if (opts.limit !== undefined) params.limit = String(opts.limit);
  const res = await api.get<Nomination[]>('/nominations', { params });
  return res.data;
}

export async function getNominationImages(uuid: string): Promise<ImageInfo[]> {
  const res = await api.get<ImageInfo[]>(`/nomination-images/${uuid}`);
  return res.data;
}

/**
 * Download an .xlsx export of ALL live entries (admin/emap only, NOC-360).
 * Fetches as a blob so the browser can save the binary; triggers a download.
 * Returns the filename used.
 */
export async function exportNominationsToExcel(): Promise<string> {
  const res = await api.get<Blob>('/nominations/export/excel', { responseType: 'blob' });
  const filename = filenameFromDisposition(
    res.headers['content-disposition'] as string | undefined,
    'waf-entries-export.xlsx'
  );
  saveBlob(res.data, filename);
  return filename;
}

/**
 * Download the WBD export CSV (NOC-396) — every live entry in the exact column
 * format WBD's WordPress wp-all-import expects. Super-admin only, enforced
 * server-side. Not password-protected (no emails/phones in this file).
 */
export async function exportNominationsToWbd(): Promise<string> {
  const res = await api.get<Blob>('/nominations/export/wbd', { responseType: 'blob' });
  const filename = filenameFromDisposition(
    res.headers['content-disposition'] as string | undefined,
    'nominations_export_for_wbd.csv'
  );
  saveBlob(res.data, filename);
  return filename;
}

/**
 * Download a TARGETED WBD export CSV — only entries with a change request
 * approved at/after `sinceIso` (an ISO-8601 UTC instant, e.g. produced by
 * Date.prototype.toISOString()). Same server-side format + super-admin gate as
 * the full WBD export. Returns the filename used for the download.
 */
export async function exportChangedNominationsToWbd(sinceIso: string): Promise<string> {
  const res = await api.get<Blob>('/nominations/export/wbd/changed', {
    params: { since: sinceIso },
    responseType: 'blob',
  });
  // Colons/dots are illegal-ish in filenames on some OSes — flatten them.
  const stamp = sinceIso.replace(/[:.]/g, '-');
  const filename = `wbd_changed_since_${stamp}.csv`;
  saveBlob(res.data, filename);
  return filename;
}

// Pull the server-provided filename out of a Content-Disposition header, falling
// back to a caller-supplied default. Handles both `filename=` and RFC 5987
// `filename*=UTF-8''…` forms.
function filenameFromDisposition(header: string | undefined, fallback: string): string {
  if (!header) return fallback;
  const star = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
    } catch {
      /* fall through to plain filename */
    }
  }
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain?.[1]?.trim() || fallback;
}

// Trigger a browser "save file" for an in-memory Blob.
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the click has been dispatched.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Download a single image for an entry through the authenticated endpoint (works
 * on both the filesystem and S3 storage backends — the bytes are streamed via
 * the API, never a public path). Admin + User only, enforced server-side.
 */
export async function downloadNominationImage(uuid: string, imageId: number): Promise<void> {
  const res = await api.get<Blob>(`/nominations/${uuid}/images/${imageId}/download`, {
    responseType: 'blob',
  });
  const name = filenameFromDisposition(
    res.headers['content-disposition'] as string | undefined,
    `${uuid}-image-${imageId}.jpg`
  );
  saveBlob(res.data, name);
}

/** Download every image for an entry as a single .zip. Admin + User only. */
export async function downloadAllNominationImages(uuid: string): Promise<void> {
  const res = await api.get<Blob>(`/nominations/${uuid}/images/download`, {
    responseType: 'blob',
    timeout: 0, // zip build + transfer can exceed the default 30s for large entries
  });
  const name = filenameFromDisposition(
    res.headers['content-disposition'] as string | undefined,
    `${uuid}-images.zip`
  );
  saveBlob(res.data, name);
}

// -------- Bulk image export (Admin tools → Download images) --------

export type ImageExportScope = 'hero' | 'all';
export type ImageExportStatus = 'queued' | 'running' | 'ready' | 'failed' | 'expired';

export interface ImageExportJob {
  id: number;
  scope: ImageExportScope;
  shortlisted_only: boolean;
  status: ImageExportStatus;
  images_total: number;
  images_done: number;
  images_failed: number;
  file_bytes: number;
  error_message: string | null;
  actor: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  expires_at: string | null;
}

/**
 * Kick off (or re-attach to) a bulk image export. The zip is built server-side,
 * so this returns as soon as the job is queued — the page then polls
 * listImageExports for progress. If a build is already running the server hands
 * back that existing job rather than starting a competing one. Admin + User only.
 */
export async function createImageExport(
  scope: ImageExportScope,
  shortlistedOnly = false
): Promise<ImageExportJob> {
  const res = await api.post<ImageExportJob>('/admin/image-exports', {
    scope,
    shortlisted_only: shortlistedOnly,
  });
  return res.data;
}

export async function listImageExports(): Promise<ImageExportJob[]> {
  const res = await api.get<ImageExportJob[]>('/admin/image-exports');
  return res.data;
}

/**
 * Download a finished export zip as a NATIVE browser download. We mint a
 * short-lived token (authenticated) and then point the browser at the
 * token-authorised URL, so Chrome streams the file straight to disk with its
 * own progress bar — nothing is buffered into a JS blob first (a full-catalogue
 * zip can be gigabytes). The response is an attachment, so the page doesn't
 * navigate and the filename comes from the server's Content-Disposition.
 */
export async function downloadImageExport(id: number): Promise<void> {
  const res = await api.post<{ token: string }>(`/admin/image-exports/${id}/download-token`);
  const url = `/api/admin/image-exports/download/${encodeURIComponent(res.data.token)}`;
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export interface UploadEntrantImageResult {
  batch_id: string;
  status: string;
  slot: string;
}

/**
 * Entrant uploads a replacement image for one slot (hero, img2..img10) on an
 * entry they own. Does NOT go live immediately — creates a change request for
 * an admin to approve/reject. 40MB max; jpg/png only (server re-validates by
 * content-type + magic bytes).
 */
export async function uploadEntrantImage(
  uuid: string,
  slot: string,
  file: File,
  batchId?: string
): Promise<UploadEntrantImageResult> {
  const form = new FormData();
  form.append('file', file);
  if (batchId) form.append('batch_id', batchId);
  const res = await api.post<UploadEntrantImageResult>(
    `/entrant/nominations/${uuid}/images/${slot}`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0 }
  );
  return res.data;
}

/**
 * Entrant requests REMOVAL of one image slot (hero, img2..img10) on an entry they
 * own. Nothing is deleted immediately — like the replacement flow it raises a
 * change request for an admin to approve/reject. On approval the slot is cleared
 * (same end state as the admin immediate-delete); on rejection the image stays.
 */
export async function deleteEntrantImage(
  uuid: string,
  slot: string,
  batchId?: string
): Promise<UploadEntrantImageResult> {
  const res = await api.delete<UploadEntrantImageResult>(
    `/entrant/nominations/${uuid}/images/${slot}`,
    { params: batchId ? { batch_id: batchId } : undefined }
  );
  return res.data;
}

/**
 * Entrant requests REMOVAL of an A2 board (a2board1 / a2board2) on an entry they
 * own. Staged as a change request (field-clear); on approval the board field is
 * cleared and the underlying PDF is deleted, on rejection the board stays.
 */
export async function deleteEntrantA2Board(
  uuid: string,
  slot: 'a2board1' | 'a2board2',
  batchId?: string
): Promise<{ batch_id: string; status: string; slot?: string; created?: number }> {
  const res = await api.delete<{ batch_id: string; status: string; slot?: string; created?: number }>(
    `/entrant/nominations/${uuid}/a2boards/${slot}`,
    { params: batchId ? { batch_id: batchId } : undefined }
  );
  return res.data;
}

export interface MediaUploadResult {
  slot: string;
  /** Served (versioned for images) URL now in effect for the slot. */
  url: string;
}

/**
 * Staff (Admin / User) IMMEDIATE replacement of one gallery image slot (hero,
 * img2..img10). Unlike the entrant flow this applies at once (no change request)
 * and is audit-logged server-side. jpg/png only (server re-validates by
 * content-type + magic bytes). Returns the new versioned served URL to cache-bust
 * the swapped image. Server enforces Admin/User only.
 */
export async function replaceNominationImage(
  uuid: string,
  slot: string,
  file: File
): Promise<MediaUploadResult> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post<MediaUploadResult>(
    `/nominations/${uuid}/images/${slot}`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0 }
  );
  return res.data;
}

/**
 * Staff (Admin / User) upload of an A2 board PDF (slot a2board1 / a2board2) to our
 * storage, replacing the free-text board URL. Applies immediately and is
 * audit-logged. PDF only (server re-validates by content-type + magic bytes).
 * Returns the served URL to point the "View A2 board" link at. Admin/User only.
 */
export async function uploadNominationA2Board(
  uuid: string,
  slot: 'a2board1' | 'a2board2',
  file: File
): Promise<MediaUploadResult> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post<MediaUploadResult>(
    `/nominations/${uuid}/a2boards/${slot}`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0 }
  );
  return res.data;
}

/**
 * Staff (Admin / User) IMMEDIATE deletion of one gallery image slot (hero,
 * img2..img10). The current image is archived to history (recoverable) then the
 * slot is cleared. Applies at once and is audit-logged server-side. Admin/User
 * only. Returns { slot, deleted: true } on success.
 */
export async function deleteNominationImage(
  uuid: string,
  slot: string
): Promise<{ slot: string; deleted: boolean }> {
  const res = await api.delete<{ slot: string; deleted: boolean }>(
    `/nominations/${uuid}/images/${slot}`
  );
  return res.data;
}

/**
 * Staff (Admin / User) IMMEDIATE deletion of an A2 board slot (a2board1 /
 * a2board2): removes the stored PDF and clears the board field. Applies at once
 * and is audit-logged. Admin/User only. Returns { slot, deleted: true }.
 */
export async function deleteNominationA2Board(
  uuid: string,
  slot: 'a2board1' | 'a2board2'
): Promise<{ slot: string; deleted: boolean }> {
  const res = await api.delete<{ slot: string; deleted: boolean }>(
    `/nominations/${uuid}/a2boards/${slot}`
  );
  return res.data;
}

export async function updateNomination(
  uuid: string,
  patch: Partial<Nomination>
): Promise<Nomination> {
  const res = await api.patch<Nomination>(`/nominations/${uuid}`, patch);
  return res.data;
}

/**
 * Staff (Admin / User) direct-edit of a single image's photographer/source
 * credit (nomination_images.image_credit — the source read by the display + WBD
 * export). Applies immediately and is audit-logged server-side. Entrants use
 * submitImageCreditChange instead (change-request gated). (NOC-397)
 */
export async function updateImageCredit(
  uuid: string,
  imageId: number,
  credit: string
): Promise<{ id: number; credit: string | null }> {
  const res = await api.patch<{ id: number; credit: string | null }>(
    `/nominations/${uuid}/images/${imageId}/credit`,
    { credit }
  );
  return res.data;
}

/**
 * Entrant proposes a new credit for one of their own image slots (hero,
 * img2..img10). Does NOT apply immediately — raises a change request for an
 * admin to approve/reject, mirroring the entrant image-replacement flow. (NOC-397)
 */
export async function submitImageCreditChange(
  uuid: string,
  slot: string,
  credit: string,
  batchId?: string
): Promise<{ batch_id: string; status: string; created: number }> {
  const res = await api.post<{ batch_id: string; status: string; created: number }>(
    `/entrant/nominations/${uuid}/images/${slot}/credit`,
    { credit, batch_id: batchId }
  );
  return res.data;
}

/**
 * Mark an entry shortlisted (or clear it). Shortlisting is a single flag
 * (`shortlisted_at`) — the one source of truth behind the list chip, the filter
 * and the "Shortlist" status. Staff (Admin / User) server-side. (NOC-380)
 */
export async function setNominationShortlisted(
  uuid: string,
  shortlisted: boolean
): Promise<Nomination> {
  const res = await api.post<Nomination>(`/nominations/${uuid}/shortlisted`, { shortlisted });
  return res.data;
}

export async function bulkDeleteNominations(
  uuids: string[],
  mode: 'soft' | 'hard'
): Promise<{ deleted: number; mode: 'soft' | 'hard' }> {
  const res = await api.post<{ deleted: number; mode: 'soft' | 'hard' }>(
    '/nominations/bulk-delete',
    { uuids, mode }
  );
  return res.data;
}

export interface AuditEntry {
  id: number;
  actor: string;
  /** Resolved display name from app_users via the row's actor_user_id FK.
   *  Null for older rows where the actor was a raw Zitadel sub or for
   *  external sources (e.g. Evessio import) with no local user. */
  actor_display_name?: string | null;
  actor_user_id?: string | null;
  source: string;
  field: string | null;
  value_before: string | number | null;
  value_after: string | number | null;
  created_at: string;
}

export async function getAuditLog(
  entityType: string,
  entityId: string
): Promise<AuditEntry[]> {
  const res = await api.get<AuditEntry[]>(`/audit/${entityType}/${entityId}`);
  return res.data;
}

export interface AuditLogRow extends AuditEntry {
  entity_type: string;
  entity_id: string;
  project_title: string | null;
  nomination_code: string | null;
  architect_company_name: string | null;
}

export async function listAuditLog(): Promise<AuditLogRow[]> {
  const res = await api.get<AuditLogRow[]>('/audit');
  return res.data;
}

// -------- Dashboard --------

export interface DashboardTotals {
  total: number;
  with_hero: number;
  with_five_plus: number;
  with_presentation: number;
  with_video: number;
}

export interface DashboardCategory {
  category_name: string;
  count: number;
}

export interface DashboardFailure {
  id: number;
  nomination_uuid: string;
  image_type: string;
  source_url: string;
  error_message: string | null;
  attempts: number;
  updated_at: string;
  project_title: string | null;
  nomination_code: string | null;
}

export interface DashboardChangeRequests {
  /** Outstanding (pending) requests awaiting review. */
  pending: number;
  approved: number;
  rejected: number;
  total: number;
}

export interface DashboardActivityPoint {
  /** ISO date (yyyy-MM-dd, UTC) for this bucket. */
  date: string;
  submitted: number;
  reviewed: number;
}

export interface DashboardUsers {
  /** Live (non-deleted) local user accounts. */
  total: number;
  /** Accounts that have logged in at least once. */
  active: number;
  /** Entrant (architect) accounts — denominator for contact_confirmed. */
  entrants: number;
  /** Entrants who have confirmed their contact details. */
  contact_confirmed: number;
}

export interface DashboardLoginPoint {
  /** ISO date (yyyy-MM-dd, UTC) for this bucket. */
  date: string;
  /** Distinct admin-portal users who logged in this day. */
  staff: number;
  /** Distinct entrant (architect) users who logged in this day. */
  architects: number;
}

export interface DashboardPayload {
  totals: DashboardTotals;
  categories: DashboardCategory[];
  images: { failed: number; recovered: number };
  recent_failures: DashboardFailure[];
  change_requests: DashboardChangeRequests;
  /** 30 daily points (oldest first) for the activity chart. */
  change_request_activity: DashboardActivityPoint[];
  users: DashboardUsers;
  /** Distinct non-empty architect company names across live entries. */
  architect_firms: number;
  /** 30 daily points (oldest first) of distinct logins, staff vs architect. */
  login_activity: DashboardLoginPoint[];
}

export async function getDashboard(): Promise<DashboardPayload> {
  const res = await api.get<DashboardPayload>('/dashboard');
  return res.data;
}

// -------- Security (admin) --------

export interface FailedLogin {
  id: number;
  /** ISO timestamp (UTC). */
  occurred_at: string;
  /** Lower-cased email the attempt was made with. */
  email: string;
  /** 'admin' | 'entrant' — which portal the attempt targeted. */
  audience: string;
}

/** Recent failed login attempts (admin only), newest first. */
export async function listFailedLogins(limit = 500): Promise<FailedLogin[]> {
  const res = await api.get<FailedLogin[]>('/security/failed-logins', { params: { limit } });
  return res.data;
}

// -------- Evessio import --------

export interface ImportOptions {
  year?: string;
  max_width?: number;
  quality_threshold_bytes?: number;
  quality_when_large?: number;
  concurrency?: number;
  limit?: number;
}

export interface Import {
  id: number;
  actor: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  year: string | null;
  entries_count: number;
  images_total: number;
  images_done: number;
  images_failed: number;
  max_width: number;
  quality_threshold_bytes: number;
  quality_when_large: number;
  concurrency: number;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface ImportImage {
  id: number;
  nomination_uuid: string;
  image_type: string;
  source_url: string;
  target_path: string;
  status: 'pending' | 'downloading' | 'compressing' | 'done' | 'failed' | 'skipped';
  bytes_in: number | null;
  bytes_out: number | null;
  attempts: number;
  error_message: string | null;
  updated_at: string;
  /** Owning entry's project title + code (left-joined) so failed images can be
   *  traced back to an entry. Null if the nomination no longer exists. */
  project_title: string | null;
  nomination_code: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
}

export async function startImport(
  entries: unknown,
  options: ImportOptions
): Promise<{ importId: number }> {
  const res = await api.post<{ importId: number }>('/imports', { entries, options }, {
    timeout: 0, // large JSON upload
  });
  return res.data;
}

export async function listImports(): Promise<Import[]> {
  const res = await api.get<Import[]>('/imports');
  return res.data;
}

export async function getImport(id: number): Promise<{ import: Import; images: ImportImage[] }> {
  const res = await api.get<{ import: Import; images: ImportImage[] }>(`/imports/${id}`);
  return res.data;
}

export async function cancelImport(id: number): Promise<void> {
  await api.post(`/imports/${id}/cancel`);
}

/** Re-run an import's image phase, picking up failed/outstanding images.
 *  Optional concurrency override (else the import's stored value is used). */
export async function resumeImport(
  id: number,
  concurrency?: number
): Promise<{ status: string; message?: string }> {
  const res = await api.post<{ status: string; message?: string }>(`/imports/${id}/resume`, {
    concurrency,
  });
  return res.data;
}

export type ImportIssueKind =
  | 'email_missing'
  | 'email_changed_on_reimport'
  | 'user_create_failed'
  | 'grant_create_failed'
  | 'order_unmatched';

export interface ImportIssue {
  id: number;
  import_id: number;
  nomination_uuid: string | null;
  kind: ImportIssueKind;
  message: string;
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
}

export async function getImportIssues(id: number): Promise<ImportIssue[]> {
  const res = await api.get<ImportIssue[]>(`/imports/${id}/issues`);
  return res.data;
}

type ResolveResult = { resolved_at: string | null; resolved_by: string | null };

/** Mark a failed import image resolved (or un-resolve). Stamps who/when server-side. */
export async function resolveImportImage(
  id: number,
  imageId: number,
  resolved: boolean
): Promise<ResolveResult> {
  const res = await api.post<ResolveResult>(`/imports/${id}/images/${imageId}/resolve`, { resolved });
  return res.data;
}

/** Mark an onboarding issue resolved (or un-resolve). */
export async function resolveImportIssue(
  id: number,
  issueId: number,
  resolved: boolean
): Promise<ResolveResult> {
  const res = await api.post<ResolveResult>(`/imports/${id}/issues/${issueId}/resolve`, { resolved });
  return res.data;
}

export interface ImportEvent {
  sequence: number;
  timestamp: string;
  message: string;
}

/** Polled while an import is running to drive the live activity tail. `sinceSeq` skips events
 *  the client has already rendered, so each poll returns only new lines. */
export async function getImportEvents(id: number, sinceSeq?: number): Promise<ImportEvent[]> {
  const res = await api.get<ImportEvent[]>(`/imports/${id}/events`, {
    params: sinceSeq !== undefined ? { sinceSeq } : undefined,
  });
  return res.data;
}

// -------- A2 board backfill (admin-only) --------

/** Status snapshot of the A2 board backfill job. `job_id` is 0 when it has never
 *  run this process lifetime (idle); negative once a run has been triggered. */
export interface A2BackfillStatus {
  job_id: number;
  state: 'idle' | 'running' | 'completed' | 'failed';
  scanned: number;
  downloaded: number;
  skipped: number;
  failed: number;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
}

/** Kick off (or no-op join) the A2 board backfill. Safe to re-run — idempotent
 *  server-side. Returns the current status (a run already in flight is returned
 *  unchanged). Admin-only; non-admins get 403. */
export async function startA2Backfill(): Promise<A2BackfillStatus> {
  const res = await api.post<A2BackfillStatus>('/a2-backfill');
  return res.data;
}

export async function getA2BackfillStatus(): Promise<A2BackfillStatus> {
  const res = await api.get<A2BackfillStatus>('/a2-backfill');
  return res.data;
}

/** Live activity tail for the current backfill run, reusing the import event shape. */
export async function getA2BackfillEvents(sinceSeq?: number): Promise<ImportEvent[]> {
  const res = await api.get<ImportEvent[]>('/a2-backfill/events', {
    params: sinceSeq !== undefined ? { sinceSeq } : undefined,
  });
  return res.data;
}

// -------- Entrant portal --------

export interface EntrantEntry {
  nomination_uuid: string;
  nomination_code: string;
  project_title: string;
  architect_company_name: string | null;
  category_name: string | null;
  project_country: string | null;
  change_requests?: Record<string, number>;
}

export interface EntrantContact {
  name: string;
  practice_name: string;
  email: string;
  phone: string;
  confirmed: boolean;
  confirmed_at: string | null;
}

export interface EntrantSession {
  entrant: {
    name: string;
    email: string | null;
  };
  contact: EntrantContact;
  entries: EntrantEntry[];
}

/** Authenticated entrant's own session. */
export async function getEntrantMe(): Promise<EntrantSession> {
  const res = await api.get<EntrantSession>('/entrant/me');
  return res.data;
}

/**
 * Entrant confirms (and optionally edits) their primary contact details.
 * Any edited fields are submitted as change requests; the call returns the
 * batch and flips the contact's confirmed flag server-side.
 */
export async function confirmEntrantDetails(body: {
  name: string;
  practice_name: string;
  email: string;
  phone: string;
}): Promise<{ batch_id: string; created: number; confirmed: boolean }> {
  const res = await api.post<{ batch_id: string; created: number; confirmed: boolean }>(
    '/entrant/confirm-details',
    body
  );
  return res.data;
}

/**
 * Admin-only: invite an external entrant and link them to nominations.
 * Idempotent: reusing an email just grants access to any new nominations.
 */
export async function inviteEntrant(input: {
  email: string;
  first_name: string;
  last_name: string;
  nomination_uuids: string[];
}): Promise<{ user_id: string; invitation_sent: boolean }> {
  const res = await api.post<{ user_id: string; invitation_sent: boolean }>(
    '/entrant/invite',
    input
  );
  return res.data;
}

// -------- Change requests --------

export interface ChangeRequest {
  id: number;
  submitter_name: string;
  submitter_email: string;
  submitter_company: string | null;
  target_entity_type: string;
  target_entity_id: string;
  field: string;
  current_value_snapshot: string | number | null;
  proposed_value: string;
  reason: string | null;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
  batch_id: string | null;
  project_title?: string | null;
  nomination_code?: string | null;
}

export interface SubmitChangeRequestInput {
  target_entity_id: string;
  changes: Record<string, string>;
  reason?: string;
  /** Optional client-minted batch id so this submission groups with the
   *  entrant's image/credit changes under one admin review. */
  batch_id?: string;
}

export async function submitChangeRequest(
  input: SubmitChangeRequestInput
): Promise<{ batch_id: string; created: number }> {
  const res = await api.post<{ batch_id: string; created: number }>(
    '/change-requests',
    input
  );
  return res.data;
}

export async function listChangeRequests(): Promise<ChangeRequest[]> {
  const res = await api.get<ChangeRequest[]>('/change-requests');
  return res.data;
}

export async function approveChangeRequest(
  id: number,
  notes?: string
): Promise<ChangeRequest> {
  const res = await api.post<ChangeRequest>(`/change-requests/${id}/approve`, {
    notes: notes || undefined,
  });
  return res.data;
}

export async function rejectChangeRequest(
  id: number,
  notes?: string
): Promise<ChangeRequest> {
  const res = await api.post<ChangeRequest>(`/change-requests/${id}/reject`, {
    notes: notes || undefined,
  });
  return res.data;
}

// -------- Users (admin only) --------

export type UserRole = 'Admin' | 'User' | 'Entrant' | 'PR';
export type UserStatus = 'Pending' | 'Active' | 'Suspended';

export interface User {
  id: string;
  email: string;
  phone: string;
  display_name: string;
  role: UserRole;
  status: UserStatus;
  created_at: string;
  last_login_at: string | null;
  details_confirmed: boolean;
}

export async function listUsers(filters?: {
  role?: UserRole;
  status?: UserStatus;
  q?: string;
}): Promise<User[]> {
  const res = await api.get<User[]>('/users', { params: filters });
  return res.data;
}

export async function createUser(input: {
  email: string;
  first_name: string;
  last_name: string;
  role: UserRole;
}): Promise<{ user: User; invitation_sent: boolean }> {
  const res = await api.post<{ user: User; invitation_sent: boolean }>('/users', input);
  return res.data;
}

export async function updateUser(
  id: string,
  input: { email?: string; first_name?: string; last_name?: string; role?: UserRole }
): Promise<User> {
  const res = await api.patch<User>(`/users/${id}`, input);
  return res.data;
}

export async function deleteUser(id: string): Promise<void> {
  await api.delete(`/users/${id}`);
}

export interface ResendInviteResult {
  message: string;
  invitation_sent: boolean;
}

export async function resendInvite(id: string): Promise<ResendInviteResult> {
  const res = await api.post<ResendInviteResult>(`/users/${id}/resend-invite`);
  return res.data;
}

export interface SendLoginLinkResult {
  kind: 'invite' | 'reset';
  invitation_sent: boolean;
  message: string;
}

/**
 * Send a self-service link that lets the user set their own password: an
 * account-setup link for a Pending user, or a password-reset link for an Active
 * user. The backend picks the right one by status.
 */
export async function sendLoginLink(id: string): Promise<SendLoginLinkResult> {
  const res = await api.post<SendLoginLinkResult>(`/users/${id}/send-login-link`);
  return res.data;
}

export async function setUserPassword(id: string, password: string): Promise<User> {
  const res = await api.post<User>(`/users/${id}/password`, { password });
  return res.data;
}

export async function setUserStatus(id: string, status: UserStatus): Promise<User> {
  const res = await api.post<User>(`/users/${id}/status`, { status });
  return res.data;
}

export interface UserNomination {
  nomination_uuid: string;
  nomination_code: string;
  project_title: string | null;
  category_name: string | null;
  project_country: string | null;
  architect_company_name: string | null;
}

export async function getUserNominations(userId: string): Promise<UserNomination[]> {
  const res = await api.get<UserNomination[]>(`/users/${userId}/nominations`);
  return res.data;
}

export async function revokeUserNomination(userId: string, nominationUuid: string): Promise<void> {
  await api.delete(`/users/${userId}/nominations/${nominationUuid}`);
}

export interface GrantUserNominationResult {
  nomination_uuid: string;
  nomination_code: string;
  project_title: string | null;
  /** True when the user already had access — endpoint is idempotent. UI uses
   *  this to differentiate "added access" from "they already had it" in the
   *  feedback toast. */
  already_granted: boolean;
}

export async function grantUserNomination(
  userId: string,
  code: string
): Promise<GrantUserNominationResult> {
  const res = await api.post<GrantUserNominationResult>(`/users/${userId}/nominations`, { code });
  return res.data;
}

// -------- Email log (admin) --------

export type EmailKind =
  | 'invite'
  | 'password_reset'
  | 'password_changed'
  | 'resend'
  | 'onboarding';
export type EmailStatus =
  | 'queued'
  | 'sending'
  | 'sent'
  | 'failed'
  | 'staged'
  | 'blocked'
  | 'discarded';

export interface EmailLogRow {
  id: number;
  recipient_email: string;
  recipient_user_id: string | null;
  kind: EmailKind;
  status: EmailStatus;
  subject: string;
  body_html: string | null;
  body_text: string | null;
  error_message: string | null;
  created_at: string;
  sent_at: string | null;
}

export interface EmailActionResult {
  id: number;
  ok: boolean;
  status: string;
  error: string | null;
}

export interface EmailLogFilters {
  recipient?: string;
  kind?: EmailKind;
  status?: EmailStatus;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export async function listEmailLog(
  filters: EmailLogFilters = {}
): Promise<{ total: number; rows: EmailLogRow[] }> {
  const res = await api.get<{ total: number; rows: EmailLogRow[] }>('/email-log', {
    params: filters,
  });
  return res.data;
}

/** Approve (dispatch) a single staged email. Still subject to the loop-guard. */
export async function approveEmail(id: number): Promise<EmailActionResult> {
  const res = await api.post<EmailActionResult>(`/email-log/${id}/approve`);
  return res.data;
}

export async function bulkApproveEmails(
  ids: number[]
): Promise<{ results: EmailActionResult[]; sent: number; total: number }> {
  const res = await api.post('/email-log/bulk-approve', { ids });
  return res.data;
}

/** Discard a single staged email so it won't be sent. */
export async function discardEmail(id: number): Promise<EmailActionResult> {
  const res = await api.post<EmailActionResult>(`/email-log/${id}/discard`);
  return res.data;
}

export async function bulkDiscardEmails(
  ids: number[]
): Promise<{ results: EmailActionResult[]; discarded: number; total: number }> {
  const res = await api.post('/email-log/bulk-discard', { ids });
  return res.data;
}

// -------- Email templates (admin) --------

export interface EmailTemplate {
  id: number;
  key: string;
  name: string;
  description: string | null;
  subject: string;
  body_html: string;
  body_text: string | null;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

/** Create/update payload. On create, `key` is required; on update it's ignored (immutable). */
export interface EmailTemplateUpsert {
  key?: string;
  name?: string;
  description?: string | null;
  subject?: string;
  body_html?: string;
  body_text?: string | null;
}

/**
 * Placeholders the onboarding renderer understands, for the editor's variable
 * hints. The canonical list lives in `./onboardingVariables` (re-exported here
 * under the historical name so existing imports keep working).
 */
export { ONBOARDING_VARIABLES as ONBOARDING_TEMPLATE_VARIABLES } from './onboardingVariables';

export async function listEmailTemplates(): Promise<EmailTemplate[]> {
  const res = await api.get<EmailTemplate[]>('/email-templates');
  return res.data;
}

export async function createEmailTemplate(input: EmailTemplateUpsert): Promise<EmailTemplate> {
  const res = await api.post<EmailTemplate>('/email-templates', input);
  return res.data;
}

export async function updateEmailTemplate(
  id: number,
  input: EmailTemplateUpsert
): Promise<EmailTemplate> {
  const res = await api.patch<EmailTemplate>(`/email-templates/${id}`, input);
  return res.data;
}

export async function deleteEmailTemplate(id: number): Promise<void> {
  await api.delete(`/email-templates/${id}`);
}

// -------- Onboarding (admin) --------

// Lowercase to match the backend: enums serialize via JsonStringEnumConverter
// with a snake_case policy, so OnboardingStatus.None -> "none", etc.
export type OnboardingStatus =
  | 'none'
  | 'assigned'
  | 'queued'
  | 'sending'
  | 'sent'
  | 'failed';

export interface OnboardingRecipient {
  user_id: string;
  email: string;
  display_name: string;
  role: string;
  user_status: string;
  template_key: string | null;
  template_name: string | null;
  onboarding_status: OnboardingStatus;
  status_detail: string | null;
  sent_at: string | null;
  categories: string[];
  project_codes: string[];
}

export interface OnboardingFilters {
  search?: string;
  role?: string;
  category?: string;
  /** A template key, or the sentinel '__none__' for "no template assigned". */
  template?: string;
  status?: OnboardingStatus;
  limit?: number;
}

export interface OnboardingEnqueueResult {
  queued: number;
  skipped_already_sent: number;
  skipped_no_template: number;
  not_found: number;
}

export interface OnboardingSummary {
  total: number;
  assigned: number;
  sent: number;
  pending: number;
  failed: number;
}

/** Sentinel template filter meaning "no template assigned" (matches backend). */
export const ONBOARDING_UNASSIGNED = '__none__';

export async function listOnboardingRecipients(
  filters: OnboardingFilters = {}
): Promise<OnboardingRecipient[]> {
  const res = await api.get<OnboardingRecipient[]>('/onboarding/recipients', { params: filters });
  return res.data;
}

export async function getOnboardingSummary(): Promise<OnboardingSummary> {
  const res = await api.get<OnboardingSummary>('/onboarding/summary');
  return res.data;
}

export async function assignOnboardingTemplate(
  userIds: string[],
  templateKey: string
): Promise<{ assigned: number }> {
  const res = await api.post('/onboarding/assign', {
    user_ids: userIds,
    template_key: templateKey,
  });
  return res.data;
}

export async function sendOnboarding(
  userIds: string[],
  force = false
): Promise<OnboardingEnqueueResult> {
  const res = await api.post<OnboardingEnqueueResult>('/onboarding/send', {
    user_ids: userIds,
    force,
  });
  return res.data;
}

// -------- Shortlist import / onboarding (admin) --------

/** One row of a judging sheet: an entry identified by `code`, plus any extra
 *  columns captured as template variables (camelCased keys). */
export interface ShortlistImportRow {
  code: string;
  vars?: Record<string, string>;
}

/** One sheet of the uploaded workbook, mapped to a known template key. */
export interface ShortlistImportSheet {
  template_key: string;
  rows: ShortlistImportRow[];
}

export interface ShortlistImportRequest {
  sheets: ShortlistImportSheet[];
}

export interface ShortlistImportPerTemplate {
  template_key: string;
  matched: number;
}

export interface ShortlistImportResult {
  matched: number;
  unmatched: number;
  templates_seen: number;
  architects_affected: number;
  unknown_template_keys: string[];
  unmatched_codes: string[];
  per_template: ShortlistImportPerTemplate[];
}

/** Per-entry status within a reviewer's tree. `status` is the send state of the
 *  result email for that entry; `status_detail` carries any error/skip reason. */
export type ShortlistAction = 'shortlist' | 'category_change' | 'entered_both';

export interface ShortlistReviewEntry {
  assignment_id: number;
  nomination_uuid: string;
  nomination_code: string;
  project_title: string;
  template_key: string;
  template_name: string;
  status: string;
  status_detail: string | null;
  sent_at: string | null;
  action: ShortlistAction;
  current_category: string | null;
  original_category: string | null;
  new_category: string | null;
  flagged: boolean;       // entered-in-both → needs a manual duplicate
  resolved: boolean;      // admin ticked the resolve box
  applied: boolean;       // category/shortlisted already written
}

/** One architect and the entries shortlisted under their account. */
export interface ShortlistReviewArchitect {
  user_id: string;
  email: string;
  display_name: string;
  user_status: string;
  account_status: string;
  account_sent_at: string | null;
  entry_count: number;
  entries_sent: number;
  entries: ShortlistReviewEntry[];
}

export interface ShortlistSendResult {
  sent: number;
  failed: number;
  skipped: number;
}

export interface ShortlistApplyResult {
  shortlisted: number;
  category_changed: number;
}

export interface ShortlistEmailPreview {
  subject: string;
  html: string;
}

/**
 * Stage a parsed judging workbook. Each sheet maps to a template key; each row
 * is an entry matched server-side by `code`. Returns a reconciliation summary
 * (matched/unmatched counts, unknown template keys, unmatched codes).
 */
export async function importShortlist(
  body: ShortlistImportRequest
): Promise<ShortlistImportResult> {
  const res = await api.post<ShortlistImportResult>('/shortlist/import', body, {
    timeout: 0, // potentially large payload
  });
  return res.data;
}

/** The review tree: architects, each with their staged entries. */
export async function getShortlistReview(): Promise<ShortlistReviewArchitect[]> {
  const res = await api.get<ShortlistReviewArchitect[]>('/shortlist/review');
  return res.data;
}

/** Send one result email per staged entry (respects the email kill-switch). */
export async function sendShortlistResults(): Promise<ShortlistSendResult> {
  // No timeout: staging hundreds of emails in one request takes well over the
  // default 30s. The backend processes the whole batch synchronously.
  const res = await api.post<ShortlistSendResult>('/shortlist/send-results', undefined, { timeout: 0 });
  return res.data;
}

/** Send one account-setup email per affected architect. */
export async function sendShortlistAccountSetup(): Promise<ShortlistSendResult> {
  const res = await api.post<ShortlistSendResult>('/shortlist/send-account-setup', undefined, { timeout: 0 });
  return res.data;
}

/** Rendered preview of the result email for a single staged entry. */
export async function previewShortlistResult(
  assignmentId: number
): Promise<ShortlistEmailPreview> {
  const res = await api.get<ShortlistEmailPreview>(
    `/shortlist/preview/result/${assignmentId}`
  );
  return res.data;
}

/** Rendered preview of the account-setup email for a single architect. */
export async function previewShortlistAccount(
  userId: string
): Promise<ShortlistEmailPreview> {
  const res = await api.get<ShortlistEmailPreview>(
    `/shortlist/preview/account/${userId}`
  );
  return res.data;
}

/** Discard all staged shortlist data so the admin can start over. */
export async function clearShortlist(): Promise<{ cleared: number }> {
  const res = await api.post<{ cleared: number }>('/shortlist/clear');
  return res.data;
}

/** The in-between data step: write new categories + add the Shortlisted tag. */
export async function applyShortlist(): Promise<ShortlistApplyResult> {
  const res = await api.post<ShortlistApplyResult>('/shortlist/apply');
  return res.data;
}

/** Toggle the "resolved" marker on an entered-in-both entry. */
export async function setShortlistResolved(
  assignmentId: number,
  resolved: boolean
): Promise<void> {
  await api.post(`/shortlist/resolve/${assignmentId}`, { resolved });
}

// -------- Create / duplicate an entry (admin) --------

export interface NominationCreate {
  nomination_code?: string | null;
  year?: string | null;
  category_name?: string | null;
  category_status?: string | null;
  project_title?: string | null;
  project_city?: string | null;
  project_country?: string | null;
  architect_company_name?: string | null;
  architect_company_country?: string | null;
  lead_architect_name?: string | null;
  client_name?: string | null;
  google_maps_link?: string | null;
  project_completion_date?: string | null;
  project_summary?: string | null;
  /** When set, copy media + architect grants from this source entry. */
  duplicate_from?: string | null;
  grant_to_user_id?: string | null;
}

/** Create a new entry (manual add, or a duplicate when duplicate_from is set). */
export async function createNomination(body: NominationCreate): Promise<Nomination> {
  const res = await api.post<Nomination>('/nominations', body);
  return res.data;
}
